import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";

import styles from "./_styles.less";
import { useSelector } from "react-redux";
import { dataAnalyticsDashboardSelector } from "@/store/dataAnalytics";
import moment from "moment";
import useFormatter from "@/hooks/useFormatter";
import { useTranslation } from "react-i18next";

const verticalLinePlugin = {
  id: "verticalLine",
  afterDatasetsDraw(chart) {
    const activeElements = chart.tooltip?.getActiveElements?.();
    if (!activeElements?.length) return;

    const { ctx } = chart;
    const x = activeElements[0].element.x;
    const topY = chart.chartArea.top;
    const bottomY = chart.chartArea.bottom;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, topY);
    ctx.lineTo(x, bottomY);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0, 0, 0, 0.33)";
    ctx.stroke();
    ctx.restore();
  },
};

export default function LineChart({ tipoData, isArmazenamento = false }) {
  const dashboardData = useSelector(dataAnalyticsDashboardSelector);
  const [dataSet, setDataSet] = useState([]);
  const { formatBytes } = useFormatter();
  const { t } = useTranslation();

  const coresPadrao = [
    { border: "#1673ff", background: "rgba(22, 115, 255, 0.3)" },
    { border: "#ff1629", background: "rgba(255, 22, 41, 0.3)" },
    { border: "#22bb33", background: "rgba(34, 187, 51, 0.3)" },
    { border: "#ffaa00", background: "rgba(255, 170, 0, 0.3)" },
  ];

  const coresAlertas = [
    { border: "#faad14", background: "#faad1434" },
    { border: "#ff4d4f", background: "#ff4d4f34" },
    { border: "#009edb", background: "#009ddb34" },
  ];

  const labelsPorTipo = {
    empresas: ["Total de Empresas"],
    alertas: ["Alertas", "Pendências", "Em dia"],
    tarefas: ["Total de Tarefas"],
    tarefasAbertas: ["Tarefas Abertas"],
    espaco: isArmazenamento ? ["Espaço usado"] : ["Documentos"],
  };

  const tituloByTipo = {
    empresas: "Número de Empresas",
    alertas: "Número de Atividades",
    tarefas: "Numero total de Tarefas",
    tarefasAbertas: "Número de Tarefas Abertas",
    espaco: isArmazenamento ? "Uso de Armazenamento" : "Número de Documentos",
  };

  useEffect(() => {
    const dadosMapped =
      dashboardData.periodos?.[tipoData]?.map((item) => {
        return {
          data: item.data,
          values:
            tipoData === "empresas"
              ? [item?.totalEmpresas]
              : tipoData === "alertas"
              ? [item?.totalAlertas, item?.totalPendencias, item?.totalEmdia]
              : tipoData === "tarefas"
              ? [item?.totalTarefas]
              : tipoData === "tarefasAbertas"
              ? [item?.totalTarefasAbertas]
              : isArmazenamento
              ? [item?.totalEspaco]
              : [item?.totalDocumentos],
        };
      }) || [];
    setDataSet(dadosMapped);
  }, [dashboardData, tipoData]);

  const lineData = useMemo(() => {
    if (dataSet.length === 0) return { labels: [], datasets: [] };

    const labelTooltip = labelsPorTipo[tipoData] || [];
    const valuesLength = dataSet[0]?.values?.length || 0;

    const labels = dataSet.map((item) => moment(item.data).utc().format("DD/MM/YYYY")); //eixo X -> datas
    const datasets = Array.from({ length: valuesLength }).map((_, indice) => {
      const cor = tipoData.includes("alertas") ? coresAlertas[indice] : coresPadrao[indice % coresPadrao.length];
      const dataValues = dataSet.map((item) => item.values[indice]); //eixo Y -> dados da API

      return {
        label: t(labelTooltip[indice]) || t(`Série ${indice + 1}`),
        data: dataValues,
        borderColor: cor.border,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) return cor.background;

          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, `${cor.border}37`);
          gradient.addColorStop(0.4, `${cor.border}22`);
          gradient.addColorStop(1, `${cor.border}00`);
          return gradient;
        },
        tension: 0.2,
        borderWidth: 3,
        fill: "origin",
      };
    });
    return { labels, datasets };
  }, [dataSet, tipoData]);

  const lineOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
        onHover: (event) => {
          event.native.target.style.cursor = "pointer";
        },
        onLeave: (event) => {
          event.native.target.style.cursor = "default";
        },
      },
      title: {
        display: true,
        text: t(tituloByTipo[tipoData]) ?? "",
        padding: {
          bottom: 30,
        },
      },
      tooltip: {
        boxWidth: 20,
        boxHeight: 10,
        usePointStyle: true,
        itemSort: (a, b) => b.parsed.y - a.parsed.y,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            if (isArmazenamento) return `${label}: ${formatBytes(value)}`;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 10 },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            if (isArmazenamento) return formatBytes(value);
            return value;
          },
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  };

  return <Line data={lineData} options={lineOptions} className={styles["chart-overlay"]} plugins={[verticalLinePlugin]} />;
}
